import { useEffect, useState } from "react"
import { Dropdown } from "react-daisyui"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import {
  setIsAuthenticated,
  setUserCompletedTutorial,
} from "../features/auth/authSlice"
import { ChoiceType } from "../features/simulation-decisions/types"
import {
  addChoice,
  addMessage,
  setSecondsElapsed,
} from "../features/simulation/simulationSlice"
import {
  createTimerWithStartTime,
  resumeTimer,
} from "../features/simulation/simulationThunks"
import { Information } from "../features/simulation/types"
import {
  convertToSeconds,
  convertToTime,
  parseTimeFromUrl,
  resetSimulationState,
  urlHasTimeReset,
} from "../features/simulation/utils"
import { MessagesWithExtraType } from "../features/simulation-messages/types"

const DevMenu = () => {
  const dispatch = useTypedDispatch()
  const simDuration = useTypedSelector((state: RootState) =>
    convertToSeconds(state.simulationSlice.duration),
  )
  const simSetup = useTypedSelector(
    (state: RootState) => state.simulationSlice.setupDone,
  )

  const [showMenu, setShowMenu] = useState<boolean>(
    process.env.NODE_ENV === "development",
  )

  const [shiftFireCount, setShiftFireCount] = useState<number>(0)

  const sampleMessageType = (type: number) => {
    const actionType =
      type === 0
        ? "incoming message"
        : type === 1
          ? "decision"
          : type === 2
            ? "fact_check"
            : "search"
    return actionType
  }

  const genSampleMessages = () => {
    // Generate random messages. There should be some messages
    // of each type (incoming, decision, fact_check, search)
    // and they should be in order of elapsed time
    let time = Math.floor(Math.random() * 1000)
    for (let i = 0; i < 10; i++) {
      const type = Math.floor(Math.random() * 4)
      const message: MessagesWithExtraType = {
        id: 0, // This will be overridden by addMessage action
        type:
          type === 0
            ? "N"
            : type === 1
              ? "decision"
              : type === 2
                ? "fact_check"
                : "search",
        elapsedTime: convertToTime(time),
        dateTime: "2021-09-01T00:00:00.000Z",
        description: `This is a sample ${sampleMessageType(type)}`,
        cell: "",
        eventId: i + "",
        title: "",
        date: "",
        response: "",
        action: 0,
        messageType: "",
        validateResponse: "",
        currentDate: "",
        categoryId: 0,
      }
      dispatch(addMessage(message))
      time += Math.floor(Math.random() * 100)
    }
    genSamplePlans()
  }

  const genSamplePlans = () => {
    // Generate random plans. There should be some plans
    // of each type (incoming, decision, fact_check, search)
    // and they should be in order of elapsed time
    let time = Math.floor(Math.random() * 1000)
    for (let i = 0; i < 10; i++) {
      const choice = {
        choiceType: ChoiceType.plan,
        item: {
          id: Math.floor(Math.random() * 1000),
          name: "This is a sample plan",
          description: "This is a sample plan",
          categories: [],
          text: "This is a sample plan",
          icon: "/simulation_decision_icon.svg",
          order: Math.floor(Math.random() * 1000),
          actionSentence: "This plan was enacted",
          planSentence: "This is a sample plan",
          isInfoSearch: 0,
          infoSearchCategory: "",
          measureReference: "",
        } as Information,
        time: time,
      }
      dispatch(addChoice(choice))
    }
    time += Math.floor(Math.random() * 100)
  }

  const handleResetSimulationStateClick = () => {
    resetSimulationState(dispatch)
  }

  const handleSetSimTime = () => {
    const time = prompt("Enter simulation time in mm:ss format")
    if (time) {
      const customTime = `00:${time}`
      const customSeconds = convertToSeconds(customTime)
      if (customSeconds < simDuration) {
        dispatch(setSecondsElapsed(customSeconds))
        dispatch(createTimerWithStartTime(customTime))
        dispatch(resumeTimer())
      }
    }
  }

  const resetTutorialCompleted = () => {
    dispatch(setUserCompletedTutorial(false))
    resetSimulationState(dispatch)
  }

  const logOutSimpleAuth = () => {
    dispatch(setIsAuthenticated(false))
  }

  useEffect(() => {
    if (!simSetup) return
    if (urlHasTimeReset()) {
      const time = parseTimeFromUrl()
      if (time < simDuration) {
        console.debug("setting time from URL", time)
        dispatch(setSecondsElapsed(time))
        dispatch(createTimerWithStartTime(convertToTime(time)))
        dispatch(resumeTimer())
        window.history.replaceState({}, "", window.location.pathname)
      }
    }
  }, [simSetup])

  useEffect(() => {
    let fireCount = 0
    const addToFireCount = (e: KeyboardEvent) => {
      if (e.shiftKey) {
        setShiftFireCount(fireCount + 1)
        fireCount++
        if (fireCount > 7) fireCount = 0
      } else {
        fireCount = 0
        setShiftFireCount(0)
      }
    }
    document.addEventListener("keydown", (e) => {
      addToFireCount(e)
    })
  }, [])

  useEffect(() => {
    if (shiftFireCount == 7) {
      setShowMenu(!showMenu)
    }
  }, [shiftFireCount])

  return (
    <div className={showMenu ? "" : "hidden"} role="pseudo-button">
      <Dropdown dataTheme="light">
        <Dropdown.Toggle button={false}>
          <div className="self-stretch rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] overflow-hidden flex flex-row items-center justify-center h-[42px] px-[18px] border-[1px] border-solid border-gray-300 cursor-pointer">
            <div className="relative leading-[28px] font-semibold">
              Dev Menu
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className="w-52 z-50">
          <Dropdown.Item onClick={genSampleMessages}>Add Mocks</Dropdown.Item>
          <Dropdown.Item onClick={handleResetSimulationStateClick}>
            Reset Simlation State
          </Dropdown.Item>
          <Dropdown.Item onClick={resetTutorialCompleted}>
            Take Tutorial
          </Dropdown.Item>
          <Dropdown.Item onClick={handleSetSimTime}>Set Sim Time</Dropdown.Item>
          <Dropdown.Item onClick={logOutSimpleAuth}>
            Log Out (Simple Auth)
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export default DevMenu
